import { PlayCircleOutlined, ReadOutlined } from "@ant-design/icons"
import { Flex, Progress, Typography, theme } from "antd"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { generateDocumentTitle } from "../../constants/document-title.ts"
import { RoutesPaths } from "../../constants/routes.ts"
import { useGuideOnboardingAssistant } from "./assistant/GuideOnboardingAssistantContext.tsx"
import { GuideConstructionVisual } from "./components/GuideConstructionVisual.tsx"
import { GuideJourneyCard } from "./components/GuideJourneyCard.tsx"
import { GuideSetupStepper } from "./components/GuideSetupStepper.tsx"
import { useGuideTour } from "./tour/GuideTourContext.tsx"
import { useProductTour } from "./tour/ProductTourContext.tsx"

const { Title, Text } = Typography

/**
 * The EZBuild Launchpad — one focused setup journey, not a dashboard of
 * repeated status. The 6-step stepper IS the setup entry point: every step
 * is clickable and takes the user straight to its real settings page (the
 * Setup Assistant panel picks up from there) — there is no intermediate
 * "here's what to configure" card on this page repeating what the stepper
 * and the Setup Assistant already say. See docs/kb for why the earlier
 * three-card / checklist / next-step-banner layout, and then the
 * current-step-card layout that replaced it, both said the same thing more
 * than once and were removed outright.
 */
export const GuideHomePage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { token } = theme.useToken()
  const { start: startProductTour } = useProductTour()
  const { activeTour: activeGuideTour, skip: skipGuideTour } = useGuideTour()
  const { items, currentItem, setupProgress, isItemComplete, openStep } =
    useGuideOnboardingAssistant()

  // The two tour engines don't guard against each other — starting the
  // Product Tour while a lesson tour is active would mount both overlays at
  // once (same fixed z-index, two sets of Next/Back/Skip controls).
  const handleStartProductTour = () => {
    if (activeGuideTour) skipGuideTour()
    startProductTour()
  }

  useEffect(() => {
    generateDocumentTitle(t("guide.home.title"))
  }, [t])

  const readyPercent = setupProgress.total
    ? Math.round((setupProgress.completed / setupProgress.total) * 100)
    : 100
  const setupComplete = !currentItem

  return (
    <Flex vertical gap={32} className="mx-auto max-w-[1440px] pb-10">
      <Flex
        align="center"
        className="overflow-hidden rounded-2xl"
        style={{
          background: `linear-gradient(135deg, ${token.colorFillQuaternary} 0%, ${token.colorBgContainer} 60%)`
        }}
      >
        <Flex vertical gap={6} className="flex-1 px-8 py-10 lg:px-12 lg:py-12">
          {setupComplete && (
            <Text
              className="text-sm font-semibold uppercase tracking-widest"
              style={{ color: token.colorPrimary }}
            >
              {t("guide.home.setupCompleteEyebrow")}
            </Text>
          )}
          <Title level={1} className="!mb-0 !text-5xl">
            {t("guide.home.title")}
          </Title>
          <Text type="secondary" className="text-2xl">
            {setupComplete ? t("guide.home.taglineReady") : t("guide.home.tagline")}
          </Text>
          {!setupComplete && (
            <Text type="secondary" className="max-w-xl text-base leading-relaxed">
              {t("guide.home.description.inProgress")}
            </Text>
          )}
        </Flex>
        <GuideConstructionVisual
          className="hidden h-64 w-[34%] shrink-0 md:block"
          tagline={t("guide.home.heroTagline")}
          fadeFrom="left"
        />
      </Flex>

      {items.length > 0 && (
        <Flex
          vertical
          gap={12}
          className="rounded-2xl border px-6 py-5"
          style={{ borderColor: token.colorBorderSecondary }}
        >
          <Flex align="center" justify="space-between" gap={24} wrap>
            <Text className="text-sm font-semibold" type="secondary">
              {t("guide.home.setupJourney")}
            </Text>
            {setupProgress.total > 0 && (
              <Flex align="center" gap={10} className="shrink-0">
                <Progress percent={readyPercent} showInfo={false} className="!w-32" />
                <Text type="secondary" className="text-sm whitespace-nowrap">
                  {t("guide.home.setupReady", {
                    completed: setupProgress.completed,
                    total: setupProgress.total
                  })}
                </Text>
                <Text type="secondary" className="text-xs whitespace-nowrap">
                  {t("guide.home.setupReadyPercent", { percent: readyPercent })}
                </Text>
              </Flex>
            )}
          </Flex>
          <GuideSetupStepper
            items={items}
            isItemComplete={isItemComplete}
            currentItemId={currentItem?.id ?? null}
            onSelect={openStep}
          />
        </Flex>
      )}

      <Flex vertical gap={4}>
        <Title level={4} className="!mb-0">
          {t("guide.home.journey.title")}
        </Title>
        <Text type="secondary">{t("guide.home.journey.subtitle")}</Text>
      </Flex>

      <Flex gap={20} wrap>
        <GuideJourneyCard
          icon={<PlayCircleOutlined className="text-xl" />}
          title={t("guide.home.whenReady.tour")}
          description={t("guide.home.whenReady.tourDescription")}
          duration={t("guide.home.whenReady.tourDuration")}
          onAction={handleStartProductTour}
        />
        <GuideJourneyCard
          icon={<ReadOutlined className="text-xl" />}
          title={t("guide.home.whenReady.learn")}
          description={t("guide.home.whenReady.learnDescription")}
          duration={t("guide.home.whenReady.learnDuration")}
          onAction={() => navigate(RoutesPaths.guide.academy)}
        />
      </Flex>
    </Flex>
  )
}
